// src/components/lekhi/LekhiTrainingPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getLekhiDayPlan,
  getLekhiSessionContent,
  saveLekhiSession,
  getTodayLekhiCompletion,
  getIndiaDate,
  checkAndUnlockAchievements,
  getFAQs,
} from '../../services/firebase/firestoreService';
import RewardScreen from '../rewards/RewardScreen';
import { getSubjectNameMr, getTopicNameMr } from '../../services/lekhiTopicNames';
import TextView from './TextView';
import AudioView from './AudioView';
import FlashcardView from './FlashcardView';
import MCQs from './MCQs';
import './LekhiTrainingPage.css';

const FALLBACK_FAQS = {
  en: [
    { q: 'Audio is not playing. What should I do?', a: 'Check your phone volume and internet connection. If the problem continues, contact your admin.' },
    { q: 'Can I redo today\'s lekhi?', a: 'No, today\'s lekhi can only be done once. You can revisit the learn content in the Library tab later.' },
    { q: 'What if I close the app in the middle?', a: 'Your progress will be lost and you will have to start over. Make sure to complete in one sitting.' }
  ],
  mr: [
    { q: 'ऑडिओ चालू होत नाही. काय करू?', a: 'फोनचा आवाज आणि इंटरनेट तपासा. समस्या कायम राहिल्यास admin शी संपर्क साधा.' },
    { q: 'आजची लेखी पुन्हा करू शकतो का?', a: 'नाही, आजची लेखी एकदाच करता येते. Library मध्ये नंतर learn content पाहता येईल.' },
    { q: 'मधेच अ‍ॅप बंद झाले तर?', a: 'तुमची प्रगती हरवेल आणि पुन्हा सुरुवात करावी लागेल. एकाच वेळी पूर्ण करा.' }
  ]
};

const PAIR_SHADES = ['#e0e7ff', '#fde2e7', '#dcfce7', '#fef3c7'];
const PAIR_TEXT = ['#3730a3', '#9f1239', '#15803d', '#92400e'];

const DonutProgress = ({ completed, total }) => {
  const size = 72;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? (completed / total) * circumference : 0;
  const offset = circumference - progress;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgb(255, 255, 255)" strokeWidth={strokeWidth}/>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#28a745" strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset 0.4s ease' }}/>
    </svg>
  );
};

const FAQModal = ({ language, faqs, onClose }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="faq-overlay" onClick={onClose}>
      <div className="faq-modal" onClick={e => e.stopPropagation()}>
        <div className="faq-header">
          <span className="faq-title">{language === 'mr' ? 'सामान्य प्रश्न' : 'FAQs'}</span>
          <button className="faq-close" onClick={onClose}>✕</button>
        </div>
        <div className="faq-list">
          {faqs.map((faq, i) => (
            <div key={i} className="faq-item">
              <button
                className={`faq-question ${openIndex === i ? 'open' : ''}`}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span>{faq.q}</span>
                <span className="faq-arrow">{openIndex === i ? '▲' : '▼'}</span>
              </button>
              {openIndex === i && <div className="faq-answer">{faq.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ConfirmBackModal = ({ language, onConfirm, onCancel }) => {
  const text = language === 'mr'
    ? { msg: 'तुमची प्रगती हरवेल. खरोखर मागे जायचे?', yes: 'होय, मागे जा', no: 'नाही, सुरू ठेवा' }
    : { msg: 'Your progress will be lost. Go back?', yes: 'Yes, go back', no: 'No, continue' };
  return (
    <div className="confirm-overlay" onClick={onCancel}>
      <div className="confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="confirm-message">{text.msg}</div>
        <div className="confirm-actions">
          <button className="confirm-cancel" onClick={onCancel}>{text.no}</button>
          <button className="confirm-yes" onClick={onConfirm}>{text.yes}</button>
        </div>
      </div>
    </div>
  );
};

const BlockPlaceholder = ({ blockTitle, language, onComplete, onBack }) => {
  const labels = language === 'mr'
    ? { back: '← मागे', markDone: 'पूर्ण झाले', placeholder: 'या ब्लॉकची UI लवकरच तयार होईल' }
    : { back: '← Back', markDone: 'Mark Complete', placeholder: 'UI for this block will be built soon' };
  return (
    <div className="lk-block-placeholder">
      <div className="lk-block-placeholder-header">
        <button className="back-btn" onClick={onBack}>{labels.back}</button>
        <div className="lk-block-placeholder-title">{blockTitle}</div>
        <div style={{ width: 60 }} />
      </div>
      <div className="lk-block-placeholder-body">
        <div className="lk-block-placeholder-text">{labels.placeholder}</div>
        <button className="lk-block-placeholder-button" onClick={onComplete}>{labels.markDone}</button>
      </div>
    </div>
  );
};

const InlineFillBlanks = ({ fillBlanks, recordItemResult, dotState, totalDots, language, onComplete, onBack }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [animating, setAnimating] = useState(false);

  const labels = language === 'mr'
    ? { back: '← मागे', title: 'रिकाम्या जागा भरा', next: 'पुढे', correct: 'अगदी बरोबर!', wrong: 'हरकत नाही', correctAnswer: 'योग्य उत्तर' }
    : { back: '← Back', title: 'Fill in the blanks', next: 'Continue', correct: 'Correct!', wrong: 'No worries', correctAnswer: 'Correct answer' };

  if (!fillBlanks || fillBlanks.length === 0) {
    return (
      <div className="ifb-container">
        <div className="ifb-header">
          <button className="back-btn" onClick={onBack}>{labels.back}</button>
          <div className="ifb-title">{labels.title}</div>
          <div style={{ width: 60 }} />
        </div>
        <div style={{ padding: 40, textAlign: 'center', color: '#6c757d' }}>No questions</div>
      </div>
    );
  }

  const item = fillBlanks[currentIndex];
  const isLastItem = currentIndex === fillBlanks.length - 1;
  const correctAnswerKey = item.correctAnswer;
  const correctAnswerText = item.options[correctAnswerKey];

  const handleWordTap = (optionKey) => {
    if (answered || animating) return;
    setSelectedAnswer(optionKey);
    setAnimating(true);
    setTimeout(() => {
      setAnimating(false);
      setAnswered(true);
      const isCorrect = optionKey === correctAnswerKey;
      if (recordItemResult) recordItemResult(item.id, isCorrect);
      if (navigator.vibrate) {
        if (isCorrect) navigator.vibrate(80);
        else navigator.vibrate([60, 50, 60, 50, 60]);
      }
    }, 280);
  };

  const handleNext = () => {
    if (isLastItem) {
      onComplete();
    } else {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setAnswered(false);
      setAnimating(false);
    }
  };

  const renderQuestionWithBlank = () => {
    const parts = item.question.split('__________');
    const fillText = selectedAnswer ? item.options[selectedAnswer] : '';
    const isCorrect = selectedAnswer === correctAnswerKey;
    let blankClass = 'ifb-blank';
    if (selectedAnswer) {
      blankClass += animating ? ' animating' : answered ? (isCorrect ? ' correct' : ' wrong') : '';
    }
    return (
      <span>
        {parts[0]}
        <span className={blankClass}>{fillText || '__________'}</span>
        {parts[1] || ''}
      </span>
    );
  };

  const getWordClass = (optionKey) => {
    if (!selectedAnswer) return 'ifb-word';
    if (optionKey === selectedAnswer) return 'ifb-word selected';
    return 'ifb-word dimmed';
  };

  const isCorrect = selectedAnswer === correctAnswerKey;

  return (
    <div className="ifb-container">
      <div className="ifb-header">
        <button className="back-btn" onClick={onBack}>{labels.back}</button>
        <div className="ifb-title">{labels.title}</div>
        <div style={{ width: 60 }} />
      </div>
      <div className="ifb-progress">
        <span className="ifb-progress-text">{currentIndex + 1} / {fillBlanks.length}</span>
        {totalDots > 0 && (
          <div className="ifb-dots">
            {Array.from({ length: totalDots }).map((_, i) => {
              const result = dotState && dotState[i];
              const cls = result === true ? 'correct' : result === false ? 'wrong' : '';
              return <span key={i} className={`ifb-dot ${cls}`} />;
            })}
          </div>
        )}
      </div>
      <div className="ifb-body">
        <div className="ifb-question">{renderQuestionWithBlank()}</div>
        <div className="ifb-words">
          {['A', 'B', 'C', 'D'].map((key) => {
            if (!item.options[key]) return null;
            return (
              <button key={key} className={getWordClass(key)} onClick={() => handleWordTap(key)} disabled={answered || animating}>
                {item.options[key]}
              </button>
            );
          })}
        </div>
        {answered && (
          <div className={`ifb-feedback ${isCorrect ? 'correct' : 'wrong'}`}>
            <div className="ifb-feedback-title">{isCorrect ? labels.correct : labels.wrong}</div>
            {!isCorrect && <div className="ifb-feedback-correct">{labels.correctAnswer}: <strong>{correctAnswerText}</strong></div>}
            {item.explanation && <div className="ifb-feedback-explanation">{item.explanation}</div>}
          </div>
        )}
      </div>
      {answered && (
        <div className="ifb-footer">
          <button className="ifb-next-btn" onClick={handleNext}>{labels.next}</button>
        </div>
      )}
    </div>
  );
};

const InlineMatchFoll = ({ matchItems, recordItemResult, dotState, totalDots, language, onComplete, onBack }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pairs, setPairs] = useState({});
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [checked, setChecked] = useState(false);
  const [shuffledRight, setShuffledRight] = useState([]);

  const labels = language === 'mr'
    ? { back: '← मागे', title: 'जुळणी करा', check: 'तपासा', reset: 'रीसेट', next: 'पुढे', correct: 'अगदी बरोबर!', wrong: 'हरकत नाही', allCorrect: 'सर्व जुळण्या योग्य आहेत.', someWrong: 'काही जुळण्या चुकल्या आहेत.' }
    : { back: '← Back', title: 'Match the following', check: 'Check', reset: 'Reset', next: 'Continue', correct: 'Correct!', wrong: 'No worries', allCorrect: 'All matches are correct.', someWrong: 'Some matches are wrong.' };

  useEffect(() => {
    if (matchItems && matchItems[currentIndex]) {
      const rights = [...matchItems[currentIndex].pairs];
      for (let i = rights.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rights[i], rights[j]] = [rights[j], rights[i]];
      }
      setShuffledRight(rights);
    }
  }, [currentIndex, matchItems]);

  if (!matchItems || matchItems.length === 0) {
    return (
      <div className="imf-container">
        <div className="imf-header">
          <button className="back-btn" onClick={onBack}>{labels.back}</button>
          <div className="imf-title">{labels.title}</div>
          <div style={{ width: 60 }} />
        </div>
        <div style={{ padding: 40, textAlign: 'center', color: '#6c757d' }}>No items</div>
      </div>
    );
  }

  const item = matchItems[currentIndex];
  const isLastItem = currentIndex === matchItems.length - 1;
  const allPaired = Object.keys(pairs).length === item.pairs.length;

  const handleLeftTap = (leftIndex) => {
    if (checked) return;
    if (pairs[leftIndex] !== undefined) {
      const newPairs = { ...pairs };
      delete newPairs[leftIndex];
      setPairs(newPairs);
      setSelectedLeft(null);
      return;
    }
    setSelectedLeft(leftIndex);
  };

  const handleRightTap = (rightIndex) => {
    if (checked) return;
    if (selectedLeft === null) return;
    const existingLeftForRight = Object.keys(pairs).find(l => pairs[l] === rightIndex);
    if (existingLeftForRight !== undefined) return;
    setPairs({ ...pairs, [selectedLeft]: rightIndex });
    setSelectedLeft(null);
  };

  const handleCheck = () => {
    if (!allPaired) return;
    setChecked(true);
    const allCorrect = Object.keys(pairs).every(leftIdx => {
      const rightIdx = pairs[leftIdx];
      const leftValue = item.pairs[leftIdx].left;
      const rightValue = shuffledRight[rightIdx].right;
      const originalPair = item.pairs.find(p => p.left === leftValue);
      return originalPair && originalPair.right === rightValue;
    });
    if (recordItemResult) recordItemResult(item.id || `match_${currentIndex}`, allCorrect);
    if (navigator.vibrate) {
      if (allCorrect) navigator.vibrate(80);
      else navigator.vibrate([60, 50, 60, 50, 60]);
    }
  };

  const handleReset = () => {
    if (checked) return;
    setPairs({});
    setSelectedLeft(null);
  };

  const handleNext = () => {
    if (isLastItem) {
      onComplete();
    } else {
      setCurrentIndex(currentIndex + 1);
      setPairs({});
      setSelectedLeft(null);
      setChecked(false);
    }
  };

  const getLeftStyle = (leftIndex) => {
    const pairedRightIdx = pairs[leftIndex];
    if (pairedRightIdx !== undefined) {
      const pairOrder = Object.keys(pairs).sort((a, b) => parseInt(a) - parseInt(b)).indexOf(String(leftIndex));
      const shadeIdx = pairOrder % PAIR_SHADES.length;
      if (checked) {
        const leftValue = item.pairs[leftIndex].left;
        const rightValue = shuffledRight[pairedRightIdx].right;
        const originalPair = item.pairs.find(p => p.left === leftValue);
        const isCorrect = originalPair && originalPair.right === rightValue;
        return {
          background: isCorrect ? '#dcfce7' : '#fce4e4',
          color: isCorrect ? '#15803d' : '#991b1b',
          borderColor: isCorrect ? '#22c55e' : '#dc3545'
        };
      }
      return { background: PAIR_SHADES[shadeIdx], color: PAIR_TEXT[shadeIdx], borderColor: PAIR_TEXT[shadeIdx] };
    }
    if (selectedLeft === leftIndex) {
      return { background: '#6366f1', color: 'white', borderColor: '#6366f1' };
    }
    return {};
  };

  const getRightStyle = (rightIndex) => {
    const leftIdx = Object.keys(pairs).find(l => pairs[l] === rightIndex);
    if (leftIdx !== undefined) {
      const pairOrder = Object.keys(pairs).sort((a, b) => parseInt(a) - parseInt(b)).indexOf(leftIdx);
      const shadeIdx = pairOrder % PAIR_SHADES.length;
      if (checked) {
        const leftValue = item.pairs[parseInt(leftIdx)].left;
        const rightValue = shuffledRight[rightIndex].right;
        const originalPair = item.pairs.find(p => p.left === leftValue);
        const isCorrect = originalPair && originalPair.right === rightValue;
        return {
          background: isCorrect ? '#dcfce7' : '#fce4e4',
          color: isCorrect ? '#15803d' : '#991b1b',
          borderColor: isCorrect ? '#22c55e' : '#dc3545'
        };
      }
      return { background: PAIR_SHADES[shadeIdx], color: PAIR_TEXT[shadeIdx], borderColor: PAIR_TEXT[shadeIdx] };
    }
    return {};
  };

  const allCorrect = checked && Object.keys(pairs).every(leftIdx => {
    const rightIdx = pairs[leftIdx];
    const leftValue = item.pairs[leftIdx].left;
    const rightValue = shuffledRight[rightIdx].right;
    const originalPair = item.pairs.find(p => p.left === leftValue);
    return originalPair && originalPair.right === rightValue;
  });

  return (
    <div className="imf-container">
      <div className="imf-header">
        <button className="back-btn" onClick={onBack}>{labels.back}</button>
        <div className="imf-title">{labels.title}</div>
        <div style={{ width: 60 }} />
      </div>

      <div className="imf-progress">
        <span className="imf-progress-text">{currentIndex + 1} / {matchItems.length}</span>
        {totalDots > 0 && (
          <div className="imf-dots">
            {Array.from({ length: totalDots }).map((_, i) => {
              const result = dotState && dotState[i];
              const cls = result === true ? 'correct' : result === false ? 'wrong' : '';
              return <span key={i} className={`imf-dot ${cls}`} />;
            })}
          </div>
        )}
      </div>

      <div className="imf-body">
        <div className="imf-columns">
          <div className="imf-column">
            {item.pairs.map((pair, leftIdx) => (
              <button
                key={leftIdx}
                className="imf-cell"
                style={getLeftStyle(leftIdx)}
                onClick={() => handleLeftTap(leftIdx)}
                disabled={checked}
              >
                {pair.left}
              </button>
            ))}
          </div>
          <div className="imf-column">
            {shuffledRight.map((pair, rightIdx) => {
              const isPaired = Object.values(pairs).includes(rightIdx);
              const disabled = checked || (!isPaired && selectedLeft === null);
              return (
                <button
                  key={rightIdx}
                  className="imf-cell"
                  style={getRightStyle(rightIdx)}
                  onClick={() => handleRightTap(rightIdx)}
                  disabled={disabled}
                >
                  {pair.right}
                </button>
              );
            })}
          </div>
        </div>

        {checked && (
          <div className={`imf-feedback ${allCorrect ? 'correct' : 'wrong'}`}>
            <div className="imf-feedback-title">{allCorrect ? labels.correct : labels.wrong}</div>
            <div className="imf-feedback-sub">{allCorrect ? labels.allCorrect : labels.someWrong}</div>
            {item.explanation && <div className="imf-feedback-explanation">{item.explanation}</div>}
          </div>
        )}
      </div>

      <div className="imf-footer">
        {!checked ? (
          <>
            <button className="imf-secondary-btn" onClick={handleReset} disabled={Object.keys(pairs).length === 0}>
              {labels.reset}
            </button>
            <button className="imf-primary-btn" onClick={handleCheck} disabled={!allPaired}>
              {labels.check}
            </button>
          </>
        ) : (
          <button className="imf-primary-btn full" onClick={handleNext}>{labels.next}</button>
        )}
      </div>
    </div>
  );
};

const TOTAL_BLOCKS = 8;

const buildBlockList = (language, sessionContent) => {
  const isMr = language === 'mr';
  return [
    { id: 'audio1', iconType: 'learn', title: isMr ? 'शिकवणी' : 'Teaching', subtitle: isMr ? 'ऑडिओ १' : 'Audio 1', type: 'audio', data: sessionContent?.audio1 },
    { id: 'flashcards', iconType: 'practice', title: isMr ? 'फ्लॅशकार्ड्स' : 'Flashcards', subtitle: `${sessionContent?.flashcards?.slice(0,2)?.length || 0} ${isMr ? 'कार्ड्स' : 'cards'}`, type: 'flashcards', data: sessionContent?.flashcards?.slice(0, 2) },
    { id: 'mcq1', iconType: 'practice', title: isMr ? 'MCQs भाग १' : 'MCQs Part 1', subtitle: `${sessionContent?.mcqs?.slice(0,2)?.length || 0} ${isMr ? 'प्रश्न' : 'questions'}`, type: 'mcq', data: sessionContent?.mcqs?.slice(0, 2) },
    { id: 'audio2', iconType: 'learn', title: isMr ? 'चुका टाळा' : 'Mistakes Drill', subtitle: isMr ? 'ऑडिओ २' : 'Audio 2', type: 'audio', data: sessionContent?.audio2 },
    { id: 'fillblanks', iconType: 'practice', title: isMr ? 'रिकाम्या जागा भरा' : 'Fill in the blanks', subtitle: `${sessionContent?.fillBlanks?.length || 0} ${isMr ? 'प्रश्न' : 'questions'}`, type: 'fillblanks', data: sessionContent?.fillBlanks },
    { id: 'match', iconType: 'practice', title: isMr ? 'जुळणी करा' : 'Match the following', subtitle: `${sessionContent?.matchFollowing?.length || 0} ${isMr ? 'जोड्या' : 'items'}`, type: 'match', data: sessionContent?.matchFollowing },
    { id: 'text', iconType: 'learn', title: isMr ? 'पाठ्यपुस्तक' : 'Text reference', subtitle: isMr ? 'वाचा' : 'Read', type: 'text', data: sessionContent?.text },
    { id: 'mcq2', iconType: 'practice', title: isMr ? 'MCQs भाग २' : 'MCQs Part 2', subtitle: `${sessionContent?.mcqs?.slice(2)?.length || 0} ${isMr ? 'प्रश्न' : 'questions'}`, type: 'mcq', data: sessionContent?.mcqs?.slice(2) }
  ];
};

const LekhiTrainingPage = ({ onBack }) => {
  const { currentUser, userData, refreshUserData } = useAuth();
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );
  const [loading, setLoading] = useState(true);
  const [emptyState, setEmptyState] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [dayPlan, setDayPlan] = useState(null);
  const [sessionContent, setSessionContent] = useState(null);
  const [completedBlocks, setCompletedBlocks] = useState(new Set());
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [dotState, setDotState] = useState([]);
  const [showFAQ, setShowFAQ] = useState(false);
  const [faqList, setFaqList] = useState(FALLBACK_FAQS[language] || FALLBACK_FAQS.mr);
  const [showConfirmBack, setShowConfirmBack] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const sessionStateRef = useRef({
    correctCount: 0,
    wrongCount: 0,
    wrongItemIds: [],
    totalQuestions: 0
  });
  const celebrationHandledRef = useRef(false);
  const rewardPreloadedRef = useRef(null);

  const translations = {
    en: {
      day: 'Day',
      markComplete: 'Start',
      completed: '✓ Done',
      locked: '🔒',
      loading: 'Loading your lekhi...',
      emptyTitle: 'Lekhi not configured for today',
      emptySub: 'Contact admin to set up today\'s lekhi.',
      contactAdmin: 'Contact Support',
      back: '← Back',
      faqs: 'FAQs'
    },
    mr: {
      day: 'दिवस',
      markComplete: 'सुरू करा',
      completed: '✓ पूर्ण',
      locked: '🔒',
      loading: 'तुमची लेखी लोड होत आहे...',
      emptyTitle: 'आजची लेखी अद्याप सेट केलेली नाही',
      emptySub: 'आजच्या लेखीसाठी admin शी संपर्क साधा.',
      contactAdmin: 'सहाय्यता संपर्क',
      back: '← मागे',
      faqs: 'सामान्य प्रश्न'
    }
  };

  const t = translations[language];

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
    setFaqList(FALLBACK_FAQS[newLang]);
  };

  const india = getIndiaDate();
  const days = language === 'en'
    ? ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
    : ['रविवार','सोमवार','मंगळवार','बुधवार','गुरुवार','शुक्रवार','शनिवार'];
  const months = language === 'en'
    ? ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    : ['जाने','फेब्रु','मार्च','एप्रिल','मे','जून','जुलै','ऑग','सप्टें','ऑक्टो','नोव्हें','डिसें'];
  const dateString = `${days[india.getDay()]}, ${months[india.getMonth()]} ${india.getDate()}`;
  const firstName = userData?.firstName || '';
  const lekhiCurrentDay = userData?.lekhiCurrentDay || 1;

  const loadSession = useCallback(async () => {
    if (!userData?.id) return;
    try {
      setLoading(true);

      // If today's Lekhi is already recorded, refuse to start a second session.
      // On a network error we deliberately fail OPEN so a blip can never lock
      // a genuine user out of training.
      let todayAlreadyDone = false;
      try {
        const todayDone = await getTodayLekhiCompletion(userData.id);
        todayAlreadyDone = !!todayDone;
      } catch (e) {
        todayAlreadyDone = false;
      }
      if (todayAlreadyDone) {
        setAlreadyDone(true);
        setLoading(false);
        return;
      }

      const plan = await getLekhiDayPlan(lekhiCurrentDay);
      if (!plan || !plan.subject || !plan.topic) {
        setEmptyState(true);
        setLoading(false);
        return;
      }
      const userLevel = userData[`${plan.subject}Level`];
      if (!userLevel) {
        setEmptyState(true);
        setLoading(false);
        return;
      }
      const content = await getLekhiSessionContent(plan.subject, plan.topic, userLevel);
      const hasAudio1 = !!content.audio1;
      const hasAudio2 = !!content.audio2;
      const hasText = !!content.text;
      const totalPractice = (content.mcqs?.length || 0) +
                            (content.fillBlanks?.length || 0) +
                            (content.matchFollowing?.length || 0) +
                            (content.flashcards?.length || 0);
      if (!hasAudio1 || !hasAudio2 || !hasText || totalPractice === 0) {
        setEmptyState(true);
        setLoading(false);
        return;
      }
      const gradedTotal = (content.mcqs?.length || 0) +
                          (content.fillBlanks?.length || 0) +
                          (content.matchFollowing?.length || 0);
      sessionStateRef.current.totalQuestions = gradedTotal;
      setDayPlan(plan);
      setSessionContent({ ...content, userLevel });
      setEmptyState(false);
    } catch (e) {
      setEmptyState(true);
    } finally {
      setLoading(false);
    }
  }, [userData, lekhiCurrentDay]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const blocks = sessionContent ? buildBlockList(language, sessionContent) : [];
  const totalDots = sessionStateRef.current.totalQuestions || 0;

  const handleBackTap = () => {
    if (showReward) {
      onBack(true);
      return;
    }
    if (loading || emptyState) {
      onBack(false);
      return;
    }
    if (completedBlocks.size === 0) {
      onBack(false);
      return;
    }
    setShowConfirmBack(true);
  };

  const handleConfirmBack = () => {
    setShowConfirmBack(false);
    onBack(false);
  };

  const handleCancelBack = () => {
    setShowConfirmBack(false);
  };

  const handleBlockTap = (block, index) => {
    if (completedBlocks.has(block.id)) return;
    const firstUncompletedIndex = blocks.findIndex(b => !completedBlocks.has(b.id));
    if (index !== firstUncompletedIndex) return;
    setActiveBlockId(block.id);
  };

  const recordItemResult = (itemId, correct) => {
    if (correct) {
      sessionStateRef.current.correctCount++;
    } else {
      sessionStateRef.current.wrongCount++;
      sessionStateRef.current.wrongItemIds.push(itemId);
    }
    setDotState(prev => [...prev, correct]);
  };

  const handleBlockSubComplete = useCallback(async () => {
    if (!activeBlockId) return;
    const newCompleted = new Set(completedBlocks);
    newCompleted.add(activeBlockId);
    setCompletedBlocks(newCompleted);
    setActiveBlockId(null);

    if (newCompleted.size >= TOTAL_BLOCKS) {
      if (celebrationHandledRef.current) return;
      celebrationHandledRef.current = true;
      try {
        const audio1Duration = sessionContent?.audio1?.durationSeconds || 0;
        const audio2Duration = sessionContent?.audio2?.durationSeconds || 0;
        const learnTimeSeconds = audio1Duration + audio2Duration + 180;
        const practiceTimeSeconds = 13 * 60;
        await saveLekhiSession(currentUser.uid, {
          dayNumber: lekhiCurrentDay,
          subject: dayPlan.subject,
          topic: dayPlan.topic,
          level: sessionContent.userLevel,
          learnTimeSeconds,
          practiceTimeSeconds,
          totalQuestions: sessionStateRef.current.totalQuestions,
          correctCount: sessionStateRef.current.correctCount,
          wrongCount: sessionStateRef.current.wrongCount,
          wrongItemIds: sessionStateRef.current.wrongItemIds
        });
        setTimeout(() => setShowReward(true), 600);
      } catch (e) {
        celebrationHandledRef.current = false;
      }
    }
  }, [activeBlockId, completedBlocks, sessionContent, dayPlan, currentUser, lekhiCurrentDay]);

  const handleBlockSubBack = () => {
    setActiveBlockId(null);
  };

  const handleFAQOpen = async () => {
    try {
      const all = await getFAQs();
      const active = all
        .filter(f => f.active === true)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      if (active.length > 0) {
        setFaqList(active.map(f => ({
          q: language === 'mr' ? f.questionMr : f.questionEn,
          a: language === 'mr' ? f.answerMr : f.answerEn
        })));
      }
    } catch (e) {}
    setShowFAQ(true);
  };

  const handleRewardComplete = async () => {
    setShowReward(false);
    try {
      const newlyUnlocked = await checkAndUnlockAchievements(currentUser.uid);
      if (newlyUnlocked.length > 0) {
        const key = `newAchievements_${currentUser.uid}`;
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        localStorage.setItem(key, JSON.stringify([...existing, ...newlyUnlocked.map(a => a.id)]));

        const celebrateOnes = newlyUnlocked.filter(a => a.celebrate === true);
        if (celebrateOnes.length > 0) {
          const celKey = `celebrateAchievements_${currentUser.uid}`;
          const existingCel = JSON.parse(localStorage.getItem(celKey) || '[]');
          const toStore = celebrateOnes.map(a => ({
            id: a.id, category: a.category, title: a.title, description: a.description
          }));
          localStorage.setItem(celKey, JSON.stringify([...existingCel, ...toStore]));
        }
      }
      if (refreshUserData) await refreshUserData();
    } catch (e) {}
    onBack(true);
  };

  const handleWhatsApp = () => {
    const message = language === 'mr'
      ? `नमस्कार, माझ्यासाठी आजची लेखी सेट केलेली नाही. लेखी दिवस ${lekhiCurrentDay}.`
      : `Hi, lekhi not set up for me today. Lekhi Day ${lekhiCurrentDay}.`;
    window.open(`https://wa.me/919359246193?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (showReward) {
    return (
      <RewardScreen
        userId={currentUser.uid}
        userData={userData}
        language={language}
        preloadedData={rewardPreloadedRef.current}
        onComplete={handleRewardComplete}
      />
    );
  }

  if (alreadyDone) {
    const msg = language === 'mr'
      ? { title: 'आजची लेखी पूर्ण झाली आहे', sub: 'उद्या पुन्हा भेटू!', btn: 'पुढे जा' }
      : { title: "Today's Lekhi is already complete", sub: 'See you tomorrow!', btn: 'Go Home' };
    return (
      <div className="lekhi-training-container">
        <div className="lk-block-list">
          <div className="lk-state-message">
            <div className="lk-state-icon">✅</div>
            <p className="lk-state-title">{msg.title}</p>
            <p className="lk-state-sub">{msg.sub}</p>
            <button
              onClick={() => onBack(true)}
              style={{ marginTop: '16px', padding: '14px 28px', background: '#007fff',
                color: '#fff', border: 'none', borderRadius: '10px',
                fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}>
              {msg.btn}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (activeBlockId) {
    const activeBlock = blocks.find(b => b.id === activeBlockId);
    const commonProps = {
      language,
      onComplete: handleBlockSubComplete,
      onBack: handleBlockSubBack
    };

    if (activeBlock?.type === 'text') {
      return <TextView textContent={activeBlock.data} {...commonProps} />;
    }
    if (activeBlock?.type === 'audio') {
      return (
        <AudioView
          audio={activeBlock.data}
          blockLabel={activeBlock.title}
          topicName={dayPlan?.topic}
          {...commonProps}
        />
      );
    }
    if (activeBlock?.type === 'flashcards') {
      return (
        <FlashcardView
          flashcards={activeBlock.data}
          {...commonProps}
        />
      );
    }
    if (activeBlock?.type === 'mcq') {
      return (
        <MCQs
          mcqs={activeBlock.data}
          recordItemResult={recordItemResult}
          dotState={dotState}
          totalDots={totalDots}
          {...commonProps}
        />
      );
    }
    if (activeBlock?.type === 'fillblanks') {
      return (
        <InlineFillBlanks
          fillBlanks={activeBlock.data}
          recordItemResult={recordItemResult}
          dotState={dotState}
          totalDots={totalDots}
          {...commonProps}
        />
      );
    }
    if (activeBlock?.type === 'match') {
      return (
        <InlineMatchFoll
          matchItems={activeBlock.data}
          recordItemResult={recordItemResult}
          dotState={dotState}
          totalDots={totalDots}
          {...commonProps}
        />
      );
    }
    return (
      <BlockPlaceholder
        blockTitle={activeBlock?.title || ''}
        language={language}
        onComplete={handleBlockSubComplete}
        onBack={handleBlockSubBack}
      />
    );
  }

  const firstUncompletedIndex = blocks.findIndex(b => !completedBlocks.has(b.id));

  return (
    <div className="lekhi-training-container">
      <div className="lk-header">
        <div className="lk-header-top">
          <button className="back-btn" onClick={handleBackTap}>{t.back}</button>
          <button className="lang-toggle-lk" onClick={handleLanguageToggle}>
            {language === 'en' ? 'मराठी' : 'EN'}
          </button>
        </div>
        <div className="lk-header-center">
          <DonutProgress completed={completedBlocks.size} total={TOTAL_BLOCKS} />
          <span className="lk-progress-text">{completedBlocks.size}/{TOTAL_BLOCKS}</span>
        </div>
        <div className="lk-header-bottom">
          <div className="lk-left">
            <div className="lk-user-name">{firstName}</div>
            <div className="lk-date">{dateString}</div>
          </div>
          <div className="lk-day-badge">{t.day} {lekhiCurrentDay}</div>
        </div>
      </div>

      {!loading && !emptyState && dayPlan && (
        <div className="lk-topic-band">
          <div className="lk-topic-band-label">आजचा विषय</div>
          <div className="lk-topic-band-value">
            {getSubjectNameMr(dayPlan.subject)} - {getTopicNameMr(dayPlan.topic)}
          </div>
        </div>
      )}

      <div className="lk-block-list">
        {loading ? (
          <div className="lk-state-message">
            <div className="lk-spinner" />
            <p>{t.loading}</p>
          </div>
        ) : emptyState ? (
          <div className="lk-state-message">
            <div className="lk-state-icon">📋</div>
            <p className="lk-state-title">{t.emptyTitle}</p>
            <p className="lk-state-sub">{t.emptySub}</p>
            <button className="whatsapp-lk-btn" onClick={handleWhatsApp}>
              {t.contactAdmin}
            </button>
          </div>
        ) : (
          <>
            {blocks.map((block, i) => {
              const isCompleted = completedBlocks.has(block.id);
              const isActive = i === firstUncompletedIndex;
              const isLocked = !isCompleted && !isActive;
              const iconSrc = isLocked ? '/lock.svg' : (block.iconType === 'learn' ? '/learn.svg' : '/practice.svg');
              return (
                <div key={block.id} className={`lk-block-card ${isCompleted ? 'completed' : ''} ${isLocked ? 'locked' : ''}`}>
                  <div className="lk-block-header">
                    <div className="lk-block-icon-wrap">
                      <img src={iconSrc} alt="" className="lk-block-icon-img" />
                    </div>
                    <div className="lk-block-info">
                      <div className="lk-block-name">{block.title}</div>
                      <div className="lk-block-subtitle">{block.subtitle}</div>
                    </div>
                  </div>
                  <div className="lk-block-controls">
                    {isCompleted ? (
                      <div className="lk-completed-indicator">{t.completed}</div>
                    ) : isLocked ? null : (
                      <button className="lk-start-btn" onClick={() => handleBlockTap(block, i)}>
                        {t.markComplete}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <button className="faq-trigger-btn" onClick={handleFAQOpen}>
              <div className="faq-trigger-icon">?</div>
            </button>
          </>
        )}
      </div>

      {showFAQ && <FAQModal language={language} faqs={faqList} onClose={() => setShowFAQ(false)} />}
      {showConfirmBack && (
        <ConfirmBackModal
          language={language}
          onConfirm={handleConfirmBack}
          onCancel={handleCancelBack}
        />
      )}
    </div>
  );
};

export default LekhiTrainingPage;